import { ScalingPolicy, WorkloadType } from "../../../../schema/types/workload/entity";
import {
  ContainerInput,
  GpuInput,
  LocalOptionsInput,
  OptionsInput,
  OptionsInputBase,
  ProbeInput,
  WorkloadInput,
} from "../../../../schema/types/workload/input";
import { convertCpuStringToNumber, convertMemoryStringToNumber } from "../../../../services/utils";
import { v4 as uuidv4 } from "uuid";
import { getDefaultBaseInput } from "../../../../schema/types/base";
import { selectContext } from "../../../../store/contextSlice";
import { store } from "../../../../store";
import { defaultValues } from "../../../../services/defaults";
import { cpuToParts, memoryToParts } from "../../../volumeset/utils";
import { getDefaultSidecarInput } from "../../../../mst/stores/cpln.jwt.client.draft";
import { getDefaultKindSelectState } from "../../../../components/antd/kindselect/KindSelect";
import { useCpFormContext } from "../../../../components/form/useCpFormContext";
import { useAppSelector } from "../../../../store/hooks";
import { shallowEqual } from "react-redux";

export function getDefaultProbeInput(): ProbeInput {
  return {
    alreadySet: false,
    exec: {
      args: [],
      command: "",
    },
    failureThreshold: "3",
    grpc: {
      port: "",
    },
    httpGet: {
      httpHeaders: [],
      path: "/",
      port: "",
      scheme: "HTTP",
    },
    initialDelaySeconds: "10",
    override: false,
    periodSeconds: "10",
    successThreshold: "1",
    tcpSocket: {
      port: "",
    },
    timeoutSeconds: "1",
    type: "http",
  };
}

export function getDefaultGpuInput(): GpuInput {
  return {
    type: "none",
    custom: {
      quantity: "1",
      resource: "",
      runtimeClass: "",
    },
    nvidia: {
      model: "t4",
      quantity: "1",
    },
  };
}

export function getDefaultContainerInput(): ContainerInput {
  const id = uuidv4();
  return {
    id: id,
    activeTabKey: `container-${0}-image`,
    name: {
      isManual: false,
      value: "",
    },
    image: {
      isExternal: false,
      cplnValue: getDefaultKindSelectState(),
      externalValue: "",
      cplnFetchAttempted: false,
      isFromSavedWorkload: false,
    },
    args: [],
    command: "",
    env: [],
    gpu: getDefaultGpuInput(),
    lifecycle: {
      postStart: {
        exec: {
          args: [],
          command: "",
          isConfigured: false,
        },
      },
      preStop: {
        exec: {
          args: [],
          command: "",
          isConfigured: false,
        },
      },
    },
    livenessProbe: getDefaultProbeInput(),
    readinessProbe: getDefaultProbeInput(),
    metrics: {
      isEnabled: false,
      path: "/metrics",
      port: "",
      dropMetrics: [],
    },
    cpu: cpuToParts(defaultValues.workload.container.cpu.default),
    memory: memoryToParts(defaultValues.workload.container.memory.default),
    minCpu: {
      amount: "",
      unit: "m",
    },
    minMemory: {
      amount: "",
      unit: "Mi",
    },
    ports: [],
    servesTraffic: false,
    volumes: [],
    workingDir: "",
    inheritEnv: false,
  };
}

export function getDefaultOptionsInputBase(): OptionsInputBase {
  const df = defaultValues.workload;

  return {
    capacityAI: true,
    capacityAIUpdateMinutes: "",
    debug: false,
    multiZone: {
      isEnabled: false,
    },
    suspend: false,
    timeoutSeconds: df.options.timeoutSeconds.default,
    autoscaling: {
      keda: "",
      metric: "disabled",
      metricPercentile: df.options.autoscaling.metricPercentile.default,
      target: df.options.autoscaling.target.default,
      minScale: df.options.autoscaling.minScale.default,
      maxScale: df.options.autoscaling.maxScale.default,
      scaleToZeroDelay: df.options.autoscaling.scaleToZeroDelay.default,
      maxConcurrency: df.options.autoscaling.maxConcurrency.default,
      multi: {
        cpu: df.options.autoscaling.target.default,
        memory: df.options.autoscaling.target.default,
        rps: "",
      },
    },
    meta: {
      kedaRaw: "",
    },
  };
}

export function getDefaultLocalOptions(options: OptionsInput): LocalOptionsInput {
  return {
    ...getDefaultOptionsInputBase(),
    id: uuidv4(),
    ...options,
    location: "",
  };
}

export function getDefaultWorkloadInput(): WorkloadInput {
  const df = defaultValues.workload;

  const defaultContainer = getDefaultContainerInput();
  defaultContainer.ports = [{ id: uuidv4(), protocol: "http", number: "8080" }];

  const context = selectContext(store.getState());

  const gvc = getDefaultKindSelectState();
  if (context.gvc) {
    gvc.value = context.gvc;
    gvc.link = `/org/${context.org}/gvc/${context.gvc}`;
  }

  return {
    ...getDefaultBaseInput(),
    version: 0,
    id: "",
    gvc: gvc,
    type: df.type.default,
    defaultOptions: {
      ...getDefaultOptionsInputBase(),
      alreadySet: true,
      override: false,
    },
    localOptions: [],
    containers: [defaultContainer],
    extras: "",
    firewallConfig: {
      external: {
        headerFilterInputHelperIsShown: false,
        httpInboundHeaderFilter: [],
        inbound: {
          allowCidr: [],
          blockedCidr: [],
          public: false,
        },
        outbound: {
          allowCidr: [],
          allowHostname: [],
          allowPort: [],
          blockedCidr: [],
          public: false,
        },
      },
      internal: {
        type: "none",
        workloads: [],
        agents: [],
        allowAccessToItself: false,
        allowKedaAccess: false,
      },
    },
    identity: {
      fixError: "",
      identityName: "",
      isFixing: false,
      isValid: false,
      isValidating: false,
      invalidError: "",
      policyName: "",
      value: getDefaultKindSelectState(),
    },
    job: {
      schedule: df.job.schedule.default,
      activeDeadlineSeconds: "",
      concurrencyPolicy: df.job.concurrencyPolicy.default,
      historyLimit: df.job.historyLimit.default,
      restartPolicy: df.job.restartPolicy.default,
    },
    loadBalancer: {
      direct: {
        ipSet: getDefaultKindSelectState(),
        isEnabled: false,
        ports: [],
      },
      geoLocation: {
        headers: {
          asn: "",
          city: "",
          country: "",
          region: "",
        },
        isEnabled: false,
      },
      replicaDirect: false,
    },
    requestRetryPolicy: {
      attempts: df.requestRetryPolicy.attempts.default,
      retryOn: df.requestRetryPolicy.retryOn.default,
    },
    rolloutOptions: {
      isEnabled: false,
      maxSurgeReplicas: df.rolloutOptions.maxSurgeReplicas.default,
      minReadySeconds: df.rolloutOptions.minReadySeconds.default,
      scalingPolicy: df.rolloutOptions.scalingPolicy.default as ScalingPolicy,
      terminationGracePeriodSeconds: df.rolloutOptions.terminationGracePeriodSeconds.default,
      maxUnavailableReplicas: "",
    },
    securityOptions: {
      filesystemGroupId: "",
      runAsUser: "",
    },
    supportDynamicTags: false,
    tags: [],
    sidecar: getDefaultSidecarInput(),
    meta: {
      activeKeyContainer: `container-${defaultContainer.id}`,
      activeKeyOption: "defaultOptions",
      isCreate: true,
      secretCache: {},
      workloadLink: "",
      initialImageFetchIsDone: true,
      showExampleAlert: true,
    },
  };
}

export function parseCPU(cpu: string): { amount: number; unit: "m" | "" } {
  if (cpu.endsWith("m")) {
    return { amount: Number(cpu.split("m")[0]), unit: "m" };
  }

  return { amount: Number(cpu), unit: "" };
}

export function parseMemory(memory: string): { isOther: boolean; amount: number; unit: "Mi" | "Gi" } {
  const validUnits = ["Mi", "Gi"];
  if (memory.includes("e")) {
    return { isOther: true, amount: 0, unit: "Mi" };
  }
  for (let unit of validUnits) {
    if (memory.endsWith(unit)) {
      try {
        let amountStr = memory.split(unit)[0];
        let amount = Number(amountStr);
        if (amountStr.includes(".") && unit !== "Gi") {
          return { isOther: true, amount: 0, unit: "Mi" };
        }
        if (amountStr.includes(".") && unit === "Gi") {
          const whole = Number(amountStr.split(".")[0]);
          const fraction = Number(amountStr.split(".")[1]);
          return { isOther: false, amount: whole * 1_000 + fraction * 100, unit: "Mi" };
        }
        return { isOther: false, amount, unit: unit as any };
      } catch (e) {
        return { isOther: true, amount: 0, unit: "Mi" };
      }
    }
  }
  return { isOther: true, amount: 0, unit: "Mi" };
}

export function tryToCore(cpuString: string): { isSuccess: boolean; value: string } {
  const UNIT_THRESHOLD = 1000;
  const cpu = convertCpuStringToNumber(cpuString);
  if (cpu > UNIT_THRESHOLD && cpu % UNIT_THRESHOLD === 0) {
    return { isSuccess: true, value: `${cpu / UNIT_THRESHOLD}` };
  }
  return { isSuccess: false, value: cpuString };
}

export function tryToGi(memoryString: string): { isSuccess: boolean; value: string } {
  const UNIT_THRESHOLD = 1024;
  const memory = convertMemoryStringToNumber(memoryString);
  if (memory > UNIT_THRESHOLD && memory % UNIT_THRESHOLD === 0) {
    return { isSuccess: true, value: `${memory / UNIT_THRESHOLD}Gi` };
  }
  return { isSuccess: false, value: memoryString };
}

export function getMetricOptions(workloadType: WorkloadType) {
  if (workloadType === "standard" || workloadType === "stateful") {
    return [
      { label: "Disabled", value: "disabled" },
      { label: "CPU Utilization", value: "cpu" },
      { label: "KEDA", value: "keda" },
      { label: "Latency", value: "latency" },
      { label: "Memory (%)", value: "memory" },
      { label: "Multi (CPU, Memory, RPS)", value: "multi" },
      { label: "Requests Per Second", value: "rps" },
    ];
  }
  if (workloadType === "serverless") {
    return [
      { label: "Disabled", value: "disabled" },
      { label: "Concurrent Requests", value: "concurrency" },
      { label: "CPU Utilization", value: "cpu" },
      { label: "Memory (%)", value: "memory" },
      { label: "Requests Per Second", value: "rps" },
    ];
  }
  return [];
}

export const secretTypeActionMap = {
  opaque: { supportsProperties: false, properties: [] },
  tls: { supportsProperties: true, properties: ["key", "cert", "chain"] },
  gcp: { supportsProperties: false, properties: [] },
  aws: {
    supportsProperties: true,
    properties: ["accessKey", "secretKey", "roleArn", "externalId"],
  },
  ecr: {
    supportsProperties: true,
    properties: ["accessKey", "secretKey", "roleArn", "externalId"],
  },
  userpass: { supportsProperties: true, properties: ["username", "password"] },
  keypair: { supportsProperties: true, properties: ["secretKey", "publicKey", "passphrase"] },
  "azure-sdk": { supportsProperties: false, properties: [] },
  "azure-connector": { supportsProperties: true, properties: ["url", "code"] },
  docker: { supportsProperties: false, properties: [] },
  dictionary: { supportsProperties: true, properties: [] },
  "nats-account": { supportsProperties: true, properties: ["accountId", "privateKey"] },
};

export const VOLUME_URI = {
  SECRET: {
    prefix: "cpln://secret/",
    label: "CPLN Secret",
    value: "{secret}[.{property}]",
  },
  K8S_SECRET: {
    prefix: "k8s://secret/",
    label: "K8s Secret",
    value: "{secret}",
  },
  VOLUMESET: {
    prefix: "cpln://volumeset/",
    label: "Volume Set",
    value: "{volumeset}",
  },
  S3: {
    prefix: "s3://",
    label: "AWS S3 Bucket",
    value: "{bucket}",
  },
  GS: {
    prefix: "gs://",
    label: "Google Bucket",
    value: "{bucket}",
  },
  AZUREBLOB: {
    prefix: "azureblob://",
    label: "Azure Blob",
    value: "{storageAccount}/{container}",
  },
  AZUREFS: {
    prefix: "azurefs://",
    label: "Azure File Share",
    value: "{storageAccount}/{share}",
  },
  SCRATCH: {
    prefix: "scratch://",
    label: "Scratch",
    value: "{name}",
  },
};

export const VolumeLimit = 15;

/// types
export type ContainerInnerTab =
  | "image" //
  | "resources"
  | "env-vars"
  | "volumes"
  | "probes"
  | "advanced-options";

export const CONTAINER_INNER_IMAGE: ContainerInnerTab = "image";
export const CONTAINER_INNER_RESOURCES: ContainerInnerTab = "resources";
export const CONTAINER_INNER_ENVVARS: ContainerInnerTab = "env-vars";
export const CONTAINER_INNER_VOLUMES: ContainerInnerTab = "volumes";
export const CONTAINER_INNER_PROBES: ContainerInnerTab = "probes";
export const CONTAINER_INNER_ADVANCED_OPTIONS: ContainerInnerTab = "advanced-options";

export function getUsedSecretLinks(containers: ContainerInput[]): string[] {
  const links: string[] = [];

  for (const c of containers) {
    for (const e of c.env) {
      if (e.prefix === "cpln://secret/") {
        const secretLink = e.cplnSecret.link;
        if (secretLink && !links.includes(secretLink)) {
          links.push(secretLink);
        }
      }
    }
    for (const v of c.volumes) {
      if (v.uriType === "cpln://secret/") {
        const secretLink = v.cplnSecret.link;
        if (secretLink && !links.includes(secretLink)) {
          links.push(secretLink);
        }
      }
    }
  }

  return links;
}

export function useUsedSecretLinks() {
  const { id } = useCpFormContext();
  return useAppSelector((state) => {
    const containers: ContainerInput[] = (state.form.instances[id].values as any).containers;
    return getUsedSecretLinks(containers);
  }, shallowEqual);
}
